/**
 * services/adaptive/adaptiveConfidence.ts
 *
 * Adaptive Confidence Overlay — MER Algo Footprints v7.1B
 *
 * Interprets raw confidence scores in light of the system's own
 * empirical calibration history. An overlay only — never changes
 * stored confidence values or any engine output.
 *
 * Non-executing. Decision support only.
 */

import type { ConfidenceCalibrationSummary, ConfidenceCalibrationBucketStats } from '../quant/confidenceCalibration';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AdaptiveConfidenceBucket {
  bucket:               string;
  rawAvgR:              number | null;
  adjustedBias:         'favorable' | 'neutral' | 'cautious' | 'insufficient_data';
  confidenceAdjustment: number;   // interpretive only: −10 to +10
  note:                 string;
}

export interface AdaptiveConfidenceSummary {
  buckets:                AdaptiveConfidenceBucket[];
  globalMode:             'trust_high_confidence' | 'use_caution' | 'mixed' | 'insufficient_data';
  suggestedAdjustmentCap: number;   // interpretive ceiling — not execution logic
  summary:                string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function bucketAdjustment(b: ConfidenceCalibrationBucketStats): {
  bias: AdaptiveConfidenceBucket['adjustedBias'];
  adj:  number;
  note: string;
} {
  if (b.status === 'insufficient_data') {
    return { bias: 'insufficient_data', adj: 0, note: 'Insufficient data — no adjustment.' };
  }
  if (b.status === 'overconfident') {
    // High-confidence tier significantly underperforms a lower tier
    const adj = b.bucket === '85+' ? -10 : -7;
    return {
      bias: 'cautious',
      adj,
      note: `Historically underperforms lower-confidence tier (gap ${b.calibrationGap?.toFixed(2) ?? '?'}R). Interpret cautiously.`,
    };
  }
  if (b.status === 'underconfident') {
    // Slight underperformance — moderate caution
    return {
      bias: 'cautious',
      adj: -4,
      note: `Slight underperformance vs prior tier. Watch for signal-quality drift.`,
    };
  }
  // well_calibrated
  if (b.avgR !== null && b.avgR > 0.3) {
    return { bias: 'favorable', adj: +5, note: 'Well-calibrated with positive expectancy in this tier.' };
  }
  if (b.avgR !== null && b.avgR > 0) {
    return { bias: 'favorable', adj: +2, note: 'Well-calibrated with modest positive expectancy.' };
  }
  return { bias: 'neutral', adj: 0, note: 'Well-calibrated. No material adjustment suggested.' };
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function computeAdaptiveConfidence(
  calibration: ConfidenceCalibrationSummary,
): AdaptiveConfidenceSummary {
  const buckets: AdaptiveConfidenceBucket[] = calibration.buckets.map(b => {
    const { bias, adj, note } = bucketAdjustment(b);
    return {
      bucket:               b.bucket,
      rawAvgR:              b.avgR,
      adjustedBias:         bias,
      confidenceAdjustment: adj,
      note,
    };
  });

  // Determine global mode from high-confidence tier behavior

  let globalMode: AdaptiveConfidenceSummary['globalMode'];
  let suggestedAdjustmentCap: number;

  if (calibration.overconfidenceRisk === 'unknown' || (calibration.overallScore === null)) {
    globalMode = 'insufficient_data';
    suggestedAdjustmentCap = 0;
  } else if (calibration.overconfidenceRisk === 'high') {
    globalMode = 'use_caution';
    suggestedAdjustmentCap = -7;
  } else if (calibration.overconfidenceRisk === 'medium') {
    globalMode = 'mixed';
    suggestedAdjustmentCap = -3;
  } else if (calibration.overallScore >= 70) {
    globalMode = 'trust_high_confidence';
    suggestedAdjustmentCap = +5;
  } else {
    globalMode = 'mixed';
    suggestedAdjustmentCap = 0;
  }

  const modeLabels: Record<AdaptiveConfidenceSummary['globalMode'], string> = {
    trust_high_confidence: 'High-confidence signals are historically well-calibrated.',
    use_caution:           'High-confidence tier has historically underperformed — apply additional scrutiny.',
    mixed:                 'Calibration is mixed across tiers — treat confidence scores as approximate.',
    insufficient_data:     'Insufficient closed-trade history for adaptive confidence adjustment.',
  };

  return {
    buckets,
    globalMode,
    suggestedAdjustmentCap,
    summary: modeLabels[globalMode],
  };
}
